/**
 * The links Fazly hands out, and the paths that have to open the app.
 *
 * Zero imports — the Android intent filters in app.config.ts, the iOS
 * association file the web app serves, and the deep-link router all have to
 * agree, and the only way they can is by reading one table.
 *
 * ─── v2 REBUILD ─────────────────────────────────────────────────────────────
 * v1's only share surface was the referral code (`User.referralCode` /
 * `referredByCode` in the recovered schema). `/scan` is declared alongside it
 * because a shared result is the obvious next one and the association file must
 * carry a path BEFORE the build that claims it ships — iOS fetches it once at
 * install and caches the outcome, failures included.
 */

export const SHARE_ORIGIN = "https://fazly.app";

/** Path prefixes that open the app instead of the browser. */
export const SHARE_PATH_PATTERNS = ["/r/*", "/scan/*", "/p/*"] as const;

/** An invite link for a referral code. */
export function referralLink(code: string): string {
  return `${SHARE_ORIGIN}/r/${encodeURIComponent(code)}`;
}

/** A link to a shared scan result. */
export function scanLink(scanId: string): string {
  return `${SHARE_ORIGIN}/scan/${encodeURIComponent(scanId)}`;
}

/**
 * A link to a community post.
 *
 * Carries the post's SHARE CODE, never its id — the reference app's SHARE-01:
 * a raw row id in a link anybody can forward is an enumeration handle into the
 * database, and a code is 72 random bits that point at one post and nothing
 * else. `/p/` rather than `/post/` because the link is typed into bios and
 * captions as often as it is tapped.
 */
export function postLink(shareCode: string): string {
  return `${SHARE_ORIGIN}/p/${encodeURIComponent(shareCode)}`;
}

export type ShareTarget =
  | { kind: "referral"; code: string }
  | { kind: "scan"; scanId: string }
  | { kind: "post"; code: string };

/**
 * Parse a path back into what it points at, or null when it points at nothing
 * we handle.
 *
 * Null rather than a throw: this runs on whatever a universal link hands us,
 * including links from an older or newer build, and an unrecognised one must
 * open the website rather than crash the app.
 */
export function parseSharePath(path: string): ShareTarget | null {
  const clean = path.split("?")[0]?.replace(/\/+$/, "") ?? "";
  const referral = /^\/r\/([A-Za-z0-9_-]{1,32})$/.exec(clean);
  if (referral?.[1]) return { kind: "referral", code: referral[1] };
  const scan = /^\/scan\/([A-Za-z0-9_-]{1,64})$/.exec(clean);
  if (scan?.[1]) return { kind: "scan", scanId: scan[1] };
  const post = /^\/p\/([A-Za-z0-9_-]{1,64})$/.exec(clean);
  if (post?.[1]) return { kind: "post", code: post[1] };
  return null;
}

/**
 * The `?s=` campaign source on an inbound share link, or null.
 *
 * Validated against a bounded alphabet and length rather than passed through:
 * it lands in an analytics property and, on the web, in rendered copy, so an
 * arbitrary query string from a link anybody can construct is untrusted input.
 */
export function shareLinkSource(search: string): string | null {
  const value = new URLSearchParams(search).get("s");
  if (!value) return null;
  return /^[a-z0-9_-]{1,32}$/i.test(value) ? value : null;
}

// ─── Post share codes ────────────────────────────────────────────────────────

/**
 * base64url's alphabet. 64 symbols → exactly 6 bits per character, so three
 * random bytes become four characters with no remainder and no rejection
 * sampling: every code is a uniform draw over the whole space.
 */
const CODE_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

/** Bytes of randomness behind one code. 72 bits — far past any birthday
 *  collision at community scale, and the unique index is the backstop. */
export const SHARE_CODE_BYTES = 9;

/** Characters in a code: 9 bytes × 8 bits ÷ 6 bits. */
export const SHARE_CODE_LENGTH = 12;

/**
 * Encode randomness as a share code.
 *
 * Takes the bytes rather than drawing them: this package is built with no DOM
 * and no node types, so it has no `crypto` to call, and a pure function is one
 * a test can pin exactly. The Worker draws the bytes from WebCrypto — never
 * `Math.random()`, which would make codes guessable in sequence.
 */
export function shareCodeFromBytes(bytes: Uint8Array): string {
  if (bytes.length < SHARE_CODE_BYTES)
    throw new Error(`a share code needs ${SHARE_CODE_BYTES} bytes`);

  let code = "";
  for (let i = 0; i < SHARE_CODE_BYTES; i += 3) {
    const triple =
      ((bytes[i] ?? 0) << 16) |
      ((bytes[i + 1] ?? 0) << 8) |
      (bytes[i + 2] ?? 0);
    for (let shift = 18; shift >= 0; shift -= 6)
      code += CODE_ALPHABET.charAt((triple >> shift) & 63);
  }
  return code;
}
